import type { CustomPayRunGateway } from './CustomPayRunGateway';
import { ApiError } from '../identity/types';
import { getMockCurrentUser } from '../identity/services/mockCurrentUser';
import { peopleGateway } from '../people';
import { employmentStatusLabels } from '../people/types';
import { compensationGateway } from '../compensation';
import type { WorkRateAssignmentResponse } from '../compensation/types';
import { mockHealthcareStore, problem } from '../healthcare-rates/mockHealthcareStore';
import { assertNoDoublePayment, resolveServiceLine } from '../healthcare-rates/mockRateResolver';
import { HdcErrorCode, SERVICE_LINE_ERROR_CODES, SERVICE_PAY_BASES, type EarningDetail, type ServiceLine } from '../healthcare-rates/types';
import { payBasisFromPayType } from '../healthcare-rates/wireMapping';
import { CustomPayRunType, customPayRunTypeLabels, MEMO_MAX_LENGTH, RUN_DISCARDED, RUN_WITHDRAWN } from './types';
import { getMockPayrollRunStatus, registerSubmittedMockRun, withdrawMockRun } from '../payroll-review/mockPayrollReviewStore';
import type {
  CalculateResponse,
  CreateCustomPayRunInput,
  CustomPayRunResponse,
  DeductionElectionWithOptOutResponse,
  DeductionOptOutInput,
  DownloadedCsv,
  DuplicateAcknowledgementInput,
  DuplicateAcknowledgementResponse,
  DuplicateFlag,
  DuplicateMatch,
  DuplicatePreviewInput,
  DuplicatePreviewResponse,
  EmployeeCalculationResponse,
  EarningLineInput,
  EarningLineResponse,
  EmployeeSelectionInput,
  EmployeeSelectionResponse,
  FinalizeForPaymentResponse,
  PayrollPreviewResponse,
  SubmitForReviewResponse,
} from './types';

const MAX_REASON_LENGTH = 500;

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** A service line as the server returns it: the inputs only (no UI-side discipline or service type snapshots). */
function withoutSnapshots(sl: ServiceLine): ServiceLine {
  const { disciplineCode: _a, disciplineName: _b, serviceTypeCode: _c, serviceTypeName: _d, ...inputs } = sl;
  void [_a, _b, _c, _d];
  return inputs;
}

/** Mock only: the people mock's seeded sample employee (Alex Example). */
const MOCK_SEEDED_PAYABLE_EMPLOYEE_IDS = ['e1'];

/** The same match rule as the server's `DuplicateEmployeeDetector.Classify`: identical period and pay day, else overlapping period (inclusive dates), else same pay day. */
function classify(a: Pick<CustomPayRunResponse, 'periodStartDate' | 'periodEndDate' | 'checkDate'>, b: Pick<CustomPayRunResponse, 'periodStartDate' | 'periodEndDate' | 'checkDate'>): DuplicateMatch | null {
  if (a.periodStartDate === b.periodStartDate && a.periodEndDate === b.periodEndDate && a.checkDate === b.checkDate) return 'IdenticalPeriodAndCheckDate';
  if (b.periodStartDate <= a.periodEndDate && b.periodEndDate >= a.periodStartDate) return 'OverlappingPeriod';
  return a.checkDate === b.checkDate ? 'SameCheckDate' : null;
}

/** CSV text cell: a leading = + - @ (or tab / CR) is neutralised with an apostrophe, then RFC 4180 quoting. Mirrors the server's exporter. */
function csvText(value: string): string {
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}


/** The server's memo rule: trimmed, blank becomes null, at most 500 characters, no control characters other than a line break (EPN-242). */
function normalizeMemo(memo: string | null | undefined): string | null {
  const text = (memo ?? '').replace(/\r\n?/g, '\n').trim();
  if (text === '') return null;
  if (text.length > MEMO_MAX_LENGTH) throw new ApiError(400, `The memo must be at most ${MEMO_MAX_LENGTH} characters.`);
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/.test(text)) throw new ApiError(400, 'The memo contains characters that are not allowed.');
  return text;
}

/** Local-development-only in-memory stand-in — the production build (VITE_USE_MOCKS=false, the default
 * in .env) always uses HttpCustomPayRunGateway against the real EPN.CustomPayRun.Api. Every synthetic
 * QA scenario in EPN/docs/custom-pay-run-v2-qa-checklist.md is run against the REAL API, never this. */
export class MockCustomPayRunGateway implements CustomPayRunGateway {
  private runs = new Map<string, CustomPayRunResponse>();
  private employees = new Map<string, EmployeeSelectionResponse[]>();
  private earnings = new Map<string, EarningLineResponse[]>();
  private deductions = new Map<string, DeductionElectionWithOptOutResponse[]>();
  private calculations = new Map<string, EmployeeCalculationResponse[]>();
  private simulatedBatches = new Map<string, FinalizeForPaymentResponse>();
  private acknowledgements = new Map<string, DuplicateAcknowledgementResponse[]>();
  private calculationsByKey = new Map<string, CalculateResponse>();
  /** Healthcare service lines: the price of each line as the last calculation resolved it (by line id), and each employee's earning details (by run, then employee). */
  private resolved = new Map<string, EarningDetail>();
  private detailsByRun = new Map<string, Map<string, EarningDetail[]>>();
  /** Module 6's coded rejection of a service line reaches the run as the employee's exception text "CODE: detail" (by run, then employee). */
  private exceptionsByRun = new Map<string, Map<string, string>>();

  async create(input: CreateCustomPayRunInput): Promise<CustomPayRunResponse> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const run: CustomPayRunResponse = {
      id, companyId: 'mock-company', runType: input.runType, payScheduleId: input.payScheduleId,
      periodStartDate: input.periodStartDate, periodEndDate: input.periodEndDate, checkDate: input.checkDate,
      description: normalizeMemo(input.description), stateCode: input.stateCode ?? 'FEDERAL', status: 'Draft',
      payrollRunId: null, payrollRunStatus: null, createdByUserId: getMockCurrentUser()?.userId ?? 'mock-user', createdByEmailSnapshot: getMockCurrentUser()?.email ?? 'mock@example.test',
      createdAtUtc: now, updatedAtUtc: now, duplicateWarnings: [],
    };
    this.runs.set(id, run);
    this.employees.set(id, []);
    this.earnings.set(id, []);
    this.deductions.set(id, []);
    return { ...run, duplicateWarnings: await this.duplicateWarnings(run) };
  }

  /** EPN-104: the real API resolves `payrollRunStatus` live from Module 7 on every read, so the mock does too —
   * from the shared mock payroll-review store — instead of freezing the value written at hand-off time. */
  private withLiveReviewStatus(run: CustomPayRunResponse): CustomPayRunResponse {
    if (!run.payrollRunId) return run;
    return { ...run, payrollRunStatus: getMockPayrollRunStatus(run.payrollRunId) ?? run.payrollRunStatus };
  }

  /** TEST-ONLY: forgets every run so one test's Custom Pay Runs cannot appear in another's list. */
  resetForTests(): void {
    this.runs.clear();
    this.employees.clear();
    this.earnings.clear();
    this.deductions.clear();
    this.calculations.clear();
    this.simulatedBatches.clear();
    this.acknowledgements.clear();
    this.calculationsByKey.clear();
    this.resolved.clear();
    this.detailsByRun.clear();
    this.exceptionsByRun.clear();
  }

  async list(): Promise<CustomPayRunResponse[]> {
    return [...this.runs.values()].map((run) => this.withLiveReviewStatus(run));
  }

  async getById(id: string): Promise<CustomPayRunResponse> {
    const run = this.runs.get(id);
    if (!run) throw new Error('Not found');
    // Like the real API, the duplicate warnings are computed live on every read (run level plus one line per employee).
    return { ...this.withLiveReviewStatus(run), duplicateWarnings: await this.duplicateWarnings(run) };
  }

  private async fullName(employeeId: string): Promise<string> {
    const e = await peopleGateway.getEmployee(employeeId).catch(() => null);
    return e ? `${e.firstName} ${e.lastName}`.trim() : 'An employee';
  }

  /** Other live runs OF THE SAME TYPE that include these employees for the same period or pay day (EPN-241: a Bonus run alongside a Regular run is not a
   * duplicate). A discarded or withdrawn run, and a handed-off run whose review run was rejected or withdrawn, no longer count. */
  private async detect(
    run: Pick<CustomPayRunResponse, 'id' | 'runType' | 'periodStartDate' | 'periodEndDate' | 'checkDate'>, employeeIds: string[],
  ): Promise<DuplicateFlag[]> {
    const flags: DuplicateFlag[] = [];
    for (const other of this.runs.values()) {
      if (other.id === run.id || other.status === RUN_DISCARDED || other.status === RUN_WITHDRAWN || other.runType !== run.runType) continue;
      const live = this.withLiveReviewStatus(other);
      if (live.payrollRunId && (live.payrollRunStatus === 'Rejected' || live.payrollRunStatus === 'Withdrawn')) continue;
      const match = classify(run, other);
      if (!match) continue;
      const included = new Set((this.employees.get(other.id) ?? []).filter((e) => e.isIncluded).map((e) => e.employeeId));
      const status = live.payrollRunId ? (live.payrollRunStatus ?? 'PendingApproval') : other.status;
      for (const employeeId of employeeIds) {
        if (!included.has(employeeId)) continue;
        const e = await peopleGateway.getEmployee(employeeId).catch(() => null);
        flags.push({
          employeeId, employeeNumber: e?.employeeNumber ?? '', firstName: e?.firstName ?? '', lastName: e?.lastName ?? '',
          otherRunKind: 'CustomPayRun', otherRunId: other.id, otherRunType: customPayRunTypeLabels[other.runType] ?? null, otherRunStatus: status,
          otherPeriodStartDate: other.periodStartDate, otherPeriodEndDate: other.periodEndDate, otherCheckDate: other.checkDate,
          match, otherRunIsApproved: status === 'Approved',
        });
      }
    }
    return flags;
  }

  private async duplicateWarnings(run: CustomPayRunResponse): Promise<string[]> {
    if (run.status === RUN_DISCARDED || run.status === RUN_WITHDRAWN || run.status === 'HandedOffToPayrollReview') return [];
    const warnings: string[] = [];
    for (const other of this.runs.values()) {
      if (other.id !== run.id && other.status !== RUN_DISCARDED && other.status !== RUN_WITHDRAWN && other.runType === run.runType && classify(run, other) === 'IdenticalPeriodAndCheckDate') {
        warnings.push(`Custom pay run ${other.id} (${customPayRunTypeLabels[other.runType]}, status ${other.status}) already covers this exact period and check date.`);
      }
    }
    const included = (this.employees.get(run.id) ?? []).filter((e) => e.isIncluded).map((e) => e.employeeId);
    for (const f of await this.detect(run, included)) {
      warnings.push(`${await this.fullName(f.employeeId)} is also in custom pay run ${f.otherRunId} (${f.otherRunType}, ${f.otherRunStatus}): ${f.match === 'IdenticalPeriodAndCheckDate' ? 'same pay period and pay day' : f.match === 'OverlappingPeriod' ? 'overlapping pay period' : 'same pay day'}.`);
    }
    return warnings;
  }

  async checkDuplicates(id: string, employeeIds?: string[]): Promise<DuplicateFlag[]> {
    const run = this.runs.get(id);
    if (!run) throw new ApiError(404, 'Not found');
    if (run.status === RUN_DISCARDED || run.status === RUN_WITHDRAWN || run.status === 'HandedOffToPayrollReview') return [];
    const ids = employeeIds ?? (this.employees.get(id) ?? []).filter((e) => e.isIncluded).map((e) => e.employeeId);
    return this.detect(run, ids);
  }

  async previewDuplicates(input: DuplicatePreviewInput): Promise<DuplicatePreviewResponse> {
    if (input.periodEndDate <= input.periodStartDate) throw new ApiError(400, 'PeriodEndDate must be after PeriodStartDate.');
    const probe = { id: '', runType: input.runType, periodStartDate: input.periodStartDate, periodEndDate: input.periodEndDate, checkDate: input.checkDate };
    const runWarnings: string[] = [];
    for (const other of this.runs.values()) {
      if (other.status !== RUN_DISCARDED && other.status !== RUN_WITHDRAWN && other.runType === input.runType && classify(probe, other) === 'IdenticalPeriodAndCheckDate') {
        runWarnings.push(`Custom pay run ${other.id} (${customPayRunTypeLabels[other.runType]}, status ${other.status}) already covers this exact period and check date.`);
      }
    }
    return { runWarnings, flags: await this.detect(probe, input.employeeIds) };
  }

  /** What the server does: only a run that was sent, only while Payroll Review still holds it pending, only its own preparer, a reason required. */
  async withdraw(id: string, reason: string): Promise<CustomPayRunResponse> {
    const run = this.runs.get(id);
    if (!run) throw new ApiError(404, 'Not found');
    const text = reason.trim();
    if (!text) throw new ApiError(400, 'A reason is required to withdraw a custom pay run.');
    if (text.length > MAX_REASON_LENGTH) throw new ApiError(400, `The reason must be at most ${MAX_REASON_LENGTH} characters.`);
    if (run.status === RUN_WITHDRAWN) return this.withLiveReviewStatus(run);
    if (run.status !== 'HandedOffToPayrollReview' || !run.payrollRunId) {
      throw new ApiError(400, run.status === RUN_DISCARDED ? 'This custom pay run was discarded. There is nothing to withdraw.' : `A custom pay run in status ${run.status} has not been sent for approval, so there is nothing to withdraw. Discard it instead.`);
    }
    withdrawMockRun(run.payrollRunId, text); // throws the same refusals as Payroll Review (not the preparer, already approved, already rejected)
    run.status = RUN_WITHDRAWN;
    run.updatedAtUtc = new Date().toISOString();
    return this.withLiveReviewStatus(run);
  }

  async getDuplicateAcknowledgements(id: string): Promise<DuplicateAcknowledgementResponse[]> {
    return this.acknowledgements.get(id) ?? [];
  }

  async discard(id: string, reason: string): Promise<CustomPayRunResponse> {
    const run = this.runs.get(id);
    if (!run) throw new ApiError(404, 'Not found');
    const text = reason.trim();
    if (!text) throw new ApiError(400, 'A reason is required to discard a custom pay run.');
    if (text.length > MAX_REASON_LENGTH) throw new ApiError(400, `The reason must be at most ${MAX_REASON_LENGTH} characters.`);
    if (run.status === RUN_DISCARDED) return run;
    if (run.status === RUN_WITHDRAWN) throw new ApiError(400, `Custom pay run ${id} was withdrawn after it was sent for approval and cannot be discarded.`);
    if (run.status === 'HandedOffToPayrollReview') {
      throw new ApiError(400, `Custom pay run ${id} was already sent to Payroll Review (Payroll Run ${run.payrollRunId}) and can no longer be discarded here. To stop it, reject the run in Payroll Review instead.`);
    }
    run.status = RUN_DISCARDED;
    run.updatedAtUtc = new Date().toISOString();
    return run;
  }

  private assertWritable(run: CustomPayRunResponse | undefined): void {
    if (run?.status === RUN_DISCARDED) throw new ApiError(400, `Custom pay run ${run.id} was discarded and is read-only. Start a new custom pay run instead.`);
    if (run?.status === RUN_WITHDRAWN) throw new ApiError(400, `Custom pay run ${run.id} was withdrawn and is read-only. Start a new custom pay run instead.`);
  }

  /** What the real server derives from Employees (the recorded method) and Compensation's readiness rule: Check is usable; Direct Deposit only when Employees says
   * its bank account can pay; no recorded method, or anything else, is NOT usable. */
  private async assessPayMethod(employeeId: string): Promise<{ kind: string | null; blocked: boolean; message: string | null }> {
    const e = await peopleGateway.getEmployee(employeeId).catch(() => null);
    // The mock directory's one seeded sample employee is treated as paid by Check, so run flows work without setup. Every other employee needs a recorded
    // pay method, as on the real server (an employee added through the UI has none until one is saved in their profile).
    if (e && MOCK_SEEDED_PAYABLE_EMPLOYEE_IDS.includes(e.id) && (e.payMethod === undefined || e.payMethod === null)) return { kind: 'Check', blocked: false, message: null };
    if (!e || e.payMethod === undefined || e.payMethod === null) return { kind: null, blocked: true, message: 'A pay method is required.' };
    if (e.payMethod === 0) return { kind: 'Check', blocked: false, message: null };
    if (e.payMethod === 1) {
      return e.payMethodReady === true
        ? { kind: 'DirectDeposit', blocked: false, message: null }
        : { kind: 'DirectDeposit', blocked: true, message: 'The direct deposit bank account is not approved or is no longer active.' };
    }
    return { kind: null, blocked: true, message: 'The pay method is not valid. Choose Check or Direct Deposit.' };
  }

  private async assertPayMethodsUsable(id: string): Promise<void> {
    const blocked: string[] = [];
    for (const e of (this.employees.get(id) ?? []).filter((x) => x.isIncluded && !this.hasNoBonusEarning(id, x.employeeId))) {
      const a = await this.assessPayMethod(e.employeeId);
      if (a.blocked) blocked.push(`${e.firstName} ${e.lastName} (${a.message})`);
    }
    if (blocked.length > 0) {
      throw new ApiError(400, `PAY_METHOD_NOT_READY: ${blocked.length} ${blocked.length === 1 ? 'employee has' : 'employees have'} no usable pay method, so this run cannot be sent for approval. Fix the pay method in the employee profile, or remove the employee from this run: ${blocked.join('; ')}`);
    }
  }

  /** The same rule as the server: every flagged employee needs an acknowledgement with a reason (and an explicit one when the other run is Approved), given now or earlier. */
  private async assertDuplicatesAcknowledged(run: CustomPayRunResponse, provided: DuplicateAcknowledgementInput[] | undefined): Promise<void> {
    const included = (this.employees.get(run.id) ?? []).filter((e) => e.isIncluded && !this.hasNoBonusEarning(run.id, e.employeeId)).map((e) => e.employeeId);
    const flags = await this.detect(run, included);
    if (flags.length === 0) return;
    const recorded = this.acknowledgements.get(run.id) ?? [];
    const unresolved: string[] = [];
    const toRecord: DuplicateAcknowledgementResponse[] = [];
    for (const f of flags) {
      const given = (provided ?? []).find((a) => a.employeeId === f.employeeId && a.otherRunId === f.otherRunId);
      const earlier = recorded.find((a) => a.employeeId === f.employeeId && a.otherRunId === f.otherRunId);
      const reason = given?.reason.trim() || earlier?.reason || '';
      const approvedAck = given ? given.acknowledgesApprovedRun : (earlier?.acknowledgesApprovedRun ?? false);
      if (!reason) {
        unresolved.push(`${f.employeeId} (already in ${f.otherRunKind} ${f.otherRunId}, ${f.otherRunStatus}): a reason is required`);
      } else if (f.otherRunIsApproved && !approvedAck) {
        unresolved.push(`${f.employeeId} (already in ${f.otherRunKind} ${f.otherRunId}, which is APPROVED): the acknowledgement must state that the other run is already approved`);
      } else if (given && !(earlier && earlier.reason === reason && earlier.acknowledgesApprovedRun === approvedAck)) {
        const user = getMockCurrentUser();
        toRecord.push({
          employeeId: f.employeeId, employeeName: `${f.firstName} ${f.lastName}`.trim(), otherRunKind: f.otherRunKind, otherRunId: f.otherRunId,
          otherRunStatus: f.otherRunStatus, match: f.match, otherRunIsApproved: f.otherRunIsApproved, acknowledgesApprovedRun: approvedAck, reason,
          acknowledgedByUserId: user?.userId ?? 'mock-user', acknowledgedByEmail: user?.email ?? 'mock@example.test', acknowledgedAtUtc: new Date().toISOString(),
        });
      }
    }
    if (unresolved.length > 0) {
      throw new ApiError(400, `DUPLICATE_ACKNOWLEDGEMENT_REQUIRED: ${unresolved.length} ${unresolved.length === 1 ? 'employee is' : 'employees are'} also in another run for the same period or pay date. Acknowledge each with a reason to continue: ${unresolved.join('; ')}`);
    }
    const next = recorded.filter((r) => !toRecord.some((t) => t.employeeId === r.employeeId && t.otherRunId === r.otherRunId));
    this.acknowledgements.set(run.id, [...next, ...toRecord]);
  }

  async exportCsv(id: string): Promise<DownloadedCsv> {
    const run = this.runs.get(id);
    if (!run) throw new ApiError(404, 'Not found');
    const preview = await this.getPreview(id);
    const rows = ['Employee Number,Employee Name,Gross Pay,Employee Taxes,Deductions Total,Net Pay,Status,Exception'];
    for (const l of preview.employees) {
      const e = await peopleGateway.getEmployee(l.employeeId).catch(() => null);
      const deductions = (l.preTaxDeductionsTotal ?? 0) + (l.postTaxDeductionsTotal ?? 0) + (l.garnishmentsTotal ?? 0);
      rows.push([
        csvText(e?.employeeNumber ?? ''), csvText(`${l.firstName} ${l.lastName}`.trim()), l.grossEarnings.toFixed(2), l.employeeTaxes.toFixed(2),
        deductions.toFixed(2), l.netPay.toFixed(2), l.exceptionFlags ? csvText("Can't Process") : 'OK', csvText(l.exceptionFlags ?? ''),
      ].join(','));
    }
    const text = `${rows.join('\r\n')}\r\n`;
    return { blob: new Blob([text], { type: 'text/csv' }), fileName: `custom-pay-run-${run.periodStartDate}-to-${run.periodEndDate}-${id.replace(/-/g, '').slice(0, 8)}.csv`, text };
  }

  /** EPN-242: the same rules as the server: only before hand-off, never on a discarded or withdrawn run, trimmed, 500 characters at most. Nothing else changes. */
  async setMemo(id: string, memo: string | null): Promise<CustomPayRunResponse> {
    const run = this.runs.get(id);
    if (!run) throw new ApiError(404, 'Not found');
    const text = normalizeMemo(memo);
    this.assertWritable(run);
    if (run.status === 'HandedOffToPayrollReview') {
      throw new ApiError(400, `Custom pay run ${id} has already been handed off to Payroll Review (Payroll Run ${run.payrollRunId}) and can no longer be modified here.`);
    }
    if (run.description !== text) {
      run.description = text;
      run.updatedAtUtc = new Date().toISOString();
    }
    return { ...this.withLiveReviewStatus(run), duplicateWarnings: await this.duplicateWarnings(run) };
  }

  /** Mirrors the real rule: an included employee who is not Active needs the explicit acknowledgment. Names and
   * status come from the (mock) people gateway, as the real API reads them from the Employees module. */
  async setEmployees(id: string, selections: EmployeeSelectionInput[]): Promise<EmployeeSelectionResponse[]> {
    this.assertWritable(this.runs.get(id));
    const rows: EmployeeSelectionResponse[] = [];
    for (const s of selections) {
      const employee = await peopleGateway.getEmployee(s.employeeId);
      const requiresAcknowledgment = employee.status !== 0;
      if (s.isIncluded && requiresAcknowledgment && !s.acknowledgeStatusWarning) {
        throw new ApiError(
          400,
          `${employee.firstName} ${employee.lastName} is ${employmentStatusLabels[employee.status] ?? 'not Active'} — set AcknowledgeStatusWarning to true to include them anyway.`,
        );
      }
      rows.push({
        employeeId: s.employeeId, employeeNumber: employee.employeeNumber, firstName: employee.firstName, lastName: employee.lastName,
        employmentStatus: employmentStatusLabels[employee.status] ?? 'Active', isIncluded: s.isIncluded,
        acknowledgedStatusWarning: s.acknowledgeStatusWarning, requiresAcknowledgment,
      });
    }
    this.employees.set(id, rows);
    // Like the real API, a changed selection makes any earlier calculation stale.
    this.calculations.delete(id);
    const run = this.runs.get(id);
    if (run) run.status = selections.some((s) => s.isIncluded) ? 'ReadyToCalculate' : 'ValidationRequired';
    return rows;
  }

  async getEmployees(id: string): Promise<EmployeeSelectionResponse[]> {
    return this.employees.get(id) ?? [];
  }

  async setEarnings(id: string, employeeId: string, lines: EarningLineInput[]): Promise<EarningLineResponse[]> {
    this.assertWritable(this.runs.get(id));
    const rows = lines.map((l) => {
      if (!l.serviceLine) return { id: crypto.randomUUID(), employeeId, ...l };
      this.assertServiceLineShape(l);
      // Like the real service, the saved line carries only what was sent: the discipline and service type names are not echoed back.
      return { id: crypto.randomUUID(), employeeId, ...l, serviceLine: withoutSnapshots(l.serviceLine) };
    });
    const existing = (this.earnings.get(id) ?? []).filter((l) => l.employeeId !== employeeId);
    this.earnings.set(id, [...existing, ...rows]);
    this.invalidateCalculation(id);
    return this.earnings.get(id)!;
  }

  async getEarnings(id: string): Promise<EarningLineResponse[]> {
    return this.earnings.get(id) ?? [];
  }

  async getDeductions(id: string): Promise<DeductionElectionWithOptOutResponse[]> {
    return this.deductions.get(id) ?? [];
  }

  async setDeductionOptOuts(id: string, employeeId: string, optOuts: DeductionOptOutInput[]): Promise<DeductionElectionWithOptOutResponse[]> {
    this.assertWritable(this.runs.get(id));
    const rows = optOuts.map((o) => ({ employeeId, deductionCodeId: 'mock-code', amountOverride: null, ...o }));
    const existing = (this.deductions.get(id) ?? []).filter((d) => d.employeeId !== employeeId);
    this.deductions.set(id, [...existing, ...rows]);
    this.invalidateCalculation(id);
    return this.deductions.get(id)!;
  }

  /** What the server refuses when a line is saved: a rate, flat amount or hours next to a service line (400 RATE_NOT_CLIENT_SUPPLIED), and an out-of-range service line (400 SERVICE_LINE_INVALID). */
  private assertServiceLineShape(line: EarningLineInput): void {
    const sl = line.serviceLine!;
    if (line.rate != null || line.flatAmount != null || line.hours != null) {
      throw problem(400, HdcErrorCode.RateNotClientSupplied, 'A service line cannot carry a rate, a flat amount or hours: EPN resolves the rate from the rate schedule. Remove them from the line.');
    }
    const invalid = (detail: string) => problem(400, HdcErrorCode.ServiceLineInvalid, detail);
    if (!sl.disciplineId) throw invalid('Choose a discipline for the service line.');
    if (!SERVICE_PAY_BASES.includes(sl.payBasis)) throw invalid('Choose a pay basis for a service line (Hourly, Per Visit, Per Shift, Daily, Per Unit/Case, Per Mile, Per Trip, Per Load, Per Class, Per Session, Per Event, Per Transaction or Fixed commission per item). Salary is not a service line basis.');
    if (!(sl.quantity > 0) || sl.quantity > 10_000 || !/^\d+(\.\d{1,4})?$/.test(String(sl.quantity))) throw invalid('The quantity must be more than 0 and at most 10,000, with up to 4 decimals.');
    if (sl.workedHours != null && (sl.workedHours < 0 || sl.workedHours > 744)) throw invalid('Worked hours must be between 0 and 744.');
    if (sl.additionalEarningAuthorized && !(sl.additionalEarningReason ?? '').trim()) throw invalid('Give a reason when a line is an additional earning.');
  }

  /**
   * Prices every service line of the included employees the way Module 6 does: the discipline must be an active assignment on the service date, no two lines may
   * pay the same work on different bases (unless both are authorised with a reason), and the rate is resolved from the schedule (never typed). The first problem
   * refuses the whole calculation, with the contract's problem code. Nothing is guessed: no rate means no price, never zero.
   */
  private async priceServiceLines(id: string, run: CustomPayRunResponse, employeeIds: string[]): Promise<void> {
    const details = new Map<string, EarningDetail[]>();
    const exceptions = new Map<string, string>();
    for (const employeeId of employeeIds) {
      try {
        await this.priceEmployee(id, run, employeeId, details);
      } catch (err) {
        // A line the server refuses (no rate, ambiguous rates, a discipline not held, double payment, an invalid line) makes THIS employee's calculation an exception: the
        // text starts with the problem code, nothing is paid for them and the other employees are not affected.
        if (err instanceof ApiError && err.code && SERVICE_LINE_ERROR_CODES.includes(err.code)) exceptions.set(employeeId, `${err.code}: ${err.message}`);
        else throw err;
      }
    }
    this.detailsByRun.set(id, details);
    this.exceptionsByRun.set(id, exceptions);
  }

  private async priceEmployee(id: string, run: CustomPayRunResponse, employeeId: string, details: Map<string, EarningDetail[]>): Promise<void> {
    {
      const lines = (this.earnings.get(id) ?? []).filter((l) => l.employeeId === employeeId);
      const service = lines.filter((l) => l.serviceLine);
      if (service.length === 0) return;
      const prepared = service.map((l) => ({ line: l.serviceLine!, serviceDate: l.serviceLine!.serviceDate ?? run.periodEndDate }));
      for (const { serviceDate } of prepared) {
        if (serviceDate < run.periodStartDate || serviceDate > run.periodEndDate) {
          throw problem(422, HdcErrorCode.ServiceLineInvalid, `The service date ${serviceDate} is outside the pay period ${run.periodStartDate} to ${run.periodEndDate}.`);
        }
      }
      assertNoDoublePayment(prepared);
      const employeeName = await this.fullName(employeeId);
      const assignments: WorkRateAssignmentResponse[] = await compensationGateway.listWorkRateAssignments(employeeId).catch(() => []);
      const out: EarningDetail[] = [];
      for (const l of lines) {
        const sl = l.serviceLine;
        if (!sl) {
          const hours = l.hours;
          out.push({
            label: l.label, amount: this.lineAmount(l), payBasis: hours != null ? 'Hourly' : null, quantity: hours, rate: l.rate, differentialKind: null, differentialValue: null,
            effectiveRate: l.rate, rateSource: 'AdHoc', rateRuleId: null, explanation: l.flatAmount != null ? `Ad hoc flat amount ${money(l.flatAmount)} entered on the pay run (not priced from a rate schedule).` : `${l.hours ?? 0} hours x ${money(l.rate ?? 0)} entered on the pay run (not priced from a rate schedule) = ${money(this.lineAmount(l))}`, disciplineId: null, disciplineCode: null,
            disciplineName: null, serviceTypeId: null, serviceTypeCode: null, serviceTypeName: null, workedHours: null, serviceDate: null,
            workLocationId: null, additionalEarningAuthorized: null, additionalEarningReason: null,
          });
          continue;
        }
        const serviceDate = sl.serviceDate ?? run.periodEndDate;
        const discipline = mockHealthcareStore.getDiscipline(sl.disciplineId);
        if (!mockHealthcareStore.activeAssignments(employeeId, serviceDate).some((a) => a.disciplineId === sl.disciplineId)) {
          throw problem(
            422, HdcErrorCode.DisciplineNotAssigned,
            `${employeeName} is not assigned ${discipline?.name ?? 'that worker category'} on ${serviceDate}, so a line cannot be paid for it. Add the worker category to the employee, or choose one they hold.`,
          );
        }
        const service = sl.serviceTypeId ? mockHealthcareStore.getServiceType(sl.serviceTypeId) : undefined;
        // Universal compensation phase C-1: with a list, the line must name one of the employee's allowed work types (no list = no limit). Checked before any rate is looked up.
        const allowed = mockHealthcareStore.allowedWorkTypes(employeeId);
        if (allowed.isRestricted) {
          if (!sl.serviceTypeId) {
            throw problem(422, HdcErrorCode.WorkTypeNotAllowed, `${employeeName} may be paid only for specific work types, so the line must name a work type. Choose one of the employee's allowed work types, or change the list.`);
          }
          if (!allowed.items.some((i) => i.serviceTypeId === sl.serviceTypeId)) {
            throw problem(422, HdcErrorCode.WorkTypeNotAllowed, `${employeeName} is not set up to be paid for the work type "${service?.name ?? 'that work type'}". Add it to the employee's allowed work types, or choose another.`);
          }
        }
        if (!discipline || !discipline.isActive) throw problem(422, HdcErrorCode.ServiceLineInvalid, 'The worker category on a line does not exist or is not active.');
        if (sl.serviceTypeId && (!service || !service.isActive)) throw problem(422, HdcErrorCode.ServiceLineInvalid, 'The work type on a line does not exist or is not active.');
        const base = assignments
          .filter((a) => a.effectiveStartDate <= serviceDate && (a.effectiveEndDate === null || a.effectiveEndDate > serviceDate))
          .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || b.effectiveStartDate.localeCompare(a.effectiveStartDate))[0];
        const detail = resolveServiceLine({
          employeeId, line: sl, serviceDate, employeeRates: mockHealthcareStore.employeeRatesOf(employeeId), companyRates: mockHealthcareStore.companyRates,
          baseRate: base ? { payBasis: payBasisFromPayType(base.payType), rate: base.rate, label: base.label } : null,
          disciplineName: discipline.name, disciplineCode: discipline.code, serviceTypeName: service?.name ?? null, serviceTypeCode: service?.code ?? null,
        });
        this.resolved.set(l.id, detail);
        out.push(detail);
      }
      details.set(employeeId, out);
    }
  }

  /** Mirrors `RevertToReadyToCalculateIfAlreadyCalculated`: editing inputs after a calculation requires recalculating. */
  private invalidateCalculation(id: string): void {
    this.detailsByRun.delete(id);
    this.exceptionsByRun.delete(id);
    for (const l of this.earnings.get(id) ?? []) this.resolved.delete(l.id);
    const run = this.runs.get(id);
    if (run && ['Calculated', 'ExceptionsPresent', 'ReadyForReview'].includes(run.status)) {
      run.status = 'ReadyToCalculate';
      this.calculations.delete(id);
    }
  }

  private lineAmount(l: EarningLineResponse): number {
    // A service line is worth what the last calculation priced it at (zero until it has been priced).
    if (l.serviceLine) return this.resolved.get(l.id)?.amount ?? 0;
    return l.flatAmount ?? (l.hours != null && l.rate != null ? l.hours * l.rate : 0);
  }

  /** Mock gross: the sum of the run's custom earning lines; an employee with none gets a fixed stand-in amount for
   * the recurring compensation the real calculation would read from Compensation. */
  private grossFor(id: string, employeeId: string): number {
    const lines = (this.earnings.get(id) ?? []).filter((l) => l.employeeId === employeeId);
    if (lines.length > 0) return lines.reduce((sum, l) => sum + this.lineAmount(l), 0);
    // EPN-241: a Bonus run starts empty; it never pulls the employee's regular pay.
    return this.runs.get(id)?.runType === CustomPayRunType.Bonus ? 0 : 500;
  }

  private hasNoBonusEarning(id: string, employeeId: string): boolean {
    return this.runs.get(id)?.runType === CustomPayRunType.Bonus && !(this.earnings.get(id) ?? []).some((l) => l.employeeId === employeeId);
  }

  async calculate(id: string, idempotencyKey?: string): Promise<CalculateResponse> {
    const run = this.runs.get(id);
    this.assertWritable(run);
    // Like the real API, the same Idempotency-Key on the same run replays the same calculations instead of making new ones.
    const replay = idempotencyKey ? this.calculationsByKey.get(`${id}:${idempotencyKey}`) : undefined;
    if (replay && run && ['Calculated', 'ExceptionsPresent', 'ReadyForReview'].includes(run.status)) return replay;
    if (run?.status === 'HandedOffToPayrollReview') {
      throw new ApiError(400, `Custom pay run ${id} has already been handed off to Payroll Review and can no longer be modified here.`);
    }
    const employees = (this.employees.get(id) ?? []).filter((e) => e.isIncluded);
    if (employees.length === 0) throw new ApiError(400, 'Cannot calculate a custom pay run with no included employees.');
    if (run) await this.priceServiceLines(id, run, employees.map((e) => e.employeeId));
    const exceptions = this.exceptionsByRun.get(id);
    const calculations = employees.map((e) =>
      exceptions?.get(e.employeeId)
        ? { employeeId: e.employeeId, payrollCalculationId: null, netPayAmount: 0, exceptionFlags: exceptions.get(e.employeeId)! }
        : this.hasNoBonusEarning(id, e.employeeId)
        ? {
            employeeId: e.employeeId, payrollCalculationId: null, netPayAmount: 0,
            exceptionFlags: 'No earnings were added for this bonus run, so there is nothing to pay. Add an earning, or remove this employee from the run.',
          }
        : { employeeId: e.employeeId, payrollCalculationId: crypto.randomUUID(), netPayAmount: this.grossFor(id, e.employeeId), exceptionFlags: null },
    );
    this.calculations.set(id, calculations);
    const status = calculations.some((c) => c.exceptionFlags) ? 'ExceptionsPresent' : 'Calculated';
    if (run) run.status = status;
    const result: CalculateResponse = { status, calculations };
    if (idempotencyKey) this.calculationsByKey.set(`${id}:${idempotencyKey}`, result);
    return result;
  }

  async acknowledgeAndProceed(id: string, acknowledgeExceptions: boolean, duplicateAcknowledgements?: DuplicateAcknowledgementInput[]): Promise<CustomPayRunResponse> {
    const run = this.runs.get(id)!;
    this.assertWritable(run);
    if (run.status === 'ExceptionsPresent' && !acknowledgeExceptions) {
      throw new ApiError(400, 'This run has exceptions — set AcknowledgeExceptions to true to proceed to review anyway.');
    }
    if (run.status !== 'Calculated' && run.status !== 'ExceptionsPresent') {
      throw new ApiError(400, `A custom pay run in status ${run.status} cannot proceed to review — calculate it first.`);
    }
    await this.assertPayMethodsUsable(id);
    await this.assertDuplicatesAcknowledged(run, duplicateAcknowledgements);
    run.status = 'ReadyForReview';
    return run;
  }

  async getPreview(id: string): Promise<PayrollPreviewResponse> {
    const run = this.runs.get(id)!;
    if (['Draft', 'ValidationRequired', 'ReadyToCalculate'].includes(run.status)) {
      throw new ApiError(400, `A custom pay run in status ${run.status} has no preview yet — calculate it first.`);
    }
    const employees = (this.employees.get(id) ?? []).filter((e) => e.isIncluded);
    const unsent = ['Calculated', 'ExceptionsPresent', 'ReadyForReview'].includes(run.status);
    const lines = [];
    for (const e of employees) {
      const serviceException = this.exceptionsByRun.get(id)?.get(e.employeeId) ?? null;
      const noEarning = this.hasNoBonusEarning(id, e.employeeId);
      const gross = noEarning || serviceException ? 0 : this.grossFor(id, e.employeeId);
      const method = await this.assessPayMethod(e.employeeId);
      const blocked = unsent && method.blocked && !noEarning && !serviceException;
      lines.push({
        employeeId: e.employeeId, firstName: e.firstName, lastName: e.lastName, payrollCalculationId: noEarning || serviceException ? null : crypto.randomUUID(),
        grossEarnings: gross, employeeTaxes: 0, employerTaxes: 0, netPay: gross,
        exceptionFlags: serviceException
          ?? (noEarning
            ? 'No earnings were added for this bonus run, so there is nothing to pay. Add an earning, or remove this employee from the run.'
            : blocked ? `No usable pay method: ${method.message}` : null),
        taxableWages: gross, preTaxDeductionsTotal: 0, postTaxDeductionsTotal: 0, garnishmentsTotal: 0, taxLines: [],
        payMethod: method.kind, payMethodBlocked: blocked, payMethodMessage: blocked ? method.message : null,
        // Only an employee with a service line has earning details here (an older run's employees look exactly as before).
        ...(this.detailsByRun.get(id)?.get(e.employeeId) ? { earningDetails: this.detailsByRun.get(id)!.get(e.employeeId)!, warnings: [] } : {}),
        ...(serviceException ? { earningDetails: [], warnings: [] } : {}),
      });
    }
    const gross = lines.reduce((sum, l) => sum + l.grossEarnings, 0);
    return {
      customPayRunId: id, status: run.status, runType: run.runType, periodStartDate: run.periodStartDate,
      periodEndDate: run.periodEndDate, checkDate: run.checkDate,
      shadowCalculationWarning: 'SHADOW CALCULATION — NOT APPROVED FOR PRODUCTION PAYROLL',
      companyGrossEarnings: gross, companyEmployeeTaxes: 0, companyEmployerTaxes: 0, companyNetPay: gross,
      employees: lines,
      createdByUserId: run.createdByUserId, createdByEmailSnapshot: run.createdByEmailSnapshot,
      createdAtUtc: run.createdAtUtc, updatedAtUtc: run.updatedAtUtc,
    };
  }

  async submitForReview(id: string, duplicateAcknowledgements?: DuplicateAcknowledgementInput[]): Promise<SubmitForReviewResponse> {
    const run = this.runs.get(id)!;
    if (run.status !== 'ReadyForReview') {
      throw new ApiError(400, `A custom pay run in status ${run.status} cannot be submitted for review — it must be ReadyForReview first.`);
    }
    await this.assertPayMethodsUsable(id);
    await this.assertDuplicatesAcknowledged(run, duplicateAcknowledgements);
    const payrollRunId = crypto.randomUUID();
    run.status = 'HandedOffToPayrollReview';
    run.payrollRunId = payrollRunId;
    run.payrollRunStatus = 'PendingApproval';
    // Mirrors the real hand-off: Module 7 gets a run created and submitted by the signed-in user, who is also
    // the creator of its calculation(s). Without a signed-in mock user (a bare unit test) a stand-in is used.
    registerSubmittedMockRun({
      id: payrollRunId, periodStartDate: run.periodStartDate, periodEndDate: run.periodEndDate, checkDate: run.checkDate,
      user: getMockCurrentUser() ?? { userId: 'mock-user', email: 'mock@example.test' },
    });
    return { customPayRunId: id, payrollRunId, payrollRunStatus: 'PendingApproval' };
  }

  /** SIMULATION ONLY, mirrors the real rules: refused until the payroll run is Approved; a repeat call surfaces the
   * same batch instead of creating another. */
  async finalizeForPayment(id: string): Promise<FinalizeForPaymentResponse> {
    const run = this.runs.get(id);
    if (!run || run.status !== 'HandedOffToPayrollReview' || !run.payrollRunId) {
      throw new ApiError(400, 'This custom pay run must be submitted for review and approved in Payroll Review first.');
    }
    const existing = this.simulatedBatches.get(id);
    if (existing) return { ...existing, alreadyFinalized: true };
    const status = getMockPayrollRunStatus(run.payrollRunId);
    if (status !== 'Approved') {
      throw new ApiError(400, `Payroll Run ${run.payrollRunId} is ${status ?? 'not found'}, not Approved yet — it must be approved in Payroll Review before it can be finalized for payment.`);
    }
    const created: FinalizeForPaymentResponse = {
      customPayRunId: id, payrollRunId: run.payrollRunId, paymentBatchId: crypto.randomUUID(),
      paymentBatchStatus: 'Submitted', isSimulation: true, alreadyFinalized: false,
    };
    this.simulatedBatches.set(id, created);
    return created;
  }
}
